import { selectedClient } from '@/core/selectedClient';
import { useAccountantReportTitle } from '@/features/tr-reportsettings/auditStatus';
import { ClientKind, useClientKind } from '@/features/tr-reportsettings/clientKind';
import type { SideMenuItem } from './SideMenu';
import {
  ACCOUNTANT_REPORT_MENU_ITEM,
  CIC34_MENU_ITEM,
  LLP_INFO_MENU_ITEM,
  REPORT_SETTINGS_LABEL,
  REPORT_SETTINGS_MENU_ITEM,
  REPORT_SETTINGS_ROUTES,
  reportSettingsMenuFor,
} from './menuItems';
import { SectionNav } from './SectionNav';

/** True on the pages of the Report Settings section (APT371): the pages the Settings menu's Report Settings entry covers. */
export const isReportSettingsRoute = (pathname: string): boolean =>
  REPORT_SETTINGS_ROUTES.some((route) => pathname.split(/[?#;]/)[0].endsWith(`/home/taxreturns/${route}`));

/**
 * APT371 (client): "Move all Report Settings options into the top row/grid selection displayed after the user selects Report Settings."
 * One bar across the top of every page of the section, in the pages' order, for the client's type (reportSettingsMenuFor: Business Info /
 * LLP Info / Company Info, no Director's Report for a sole trader or partnership, Members' Report for an LLP, ...), Auditor's Report when the
 * accounts are audited, and CIC34 Report for a community interest company.
 */
export function ReportSettingsNav() {
  const clientId = selectedClient()?.id;
  const kind = useClientKind(clientId);
  const clientType = kind?.clientType ?? ClientKind.LimitedCompany;
  const accountantReportTitle = useAccountantReportTitle(clientId);

  const items: SideMenuItem[] = reportSettingsMenuFor(clientType).map((item) => {
    if (item.to === ACCOUNTANT_REPORT_MENU_ITEM.to) return { ...item, label: accountantReportTitle };
    // The first page is also opened by the earlier build's `llp-info` address.
    if (item.to === REPORT_SETTINGS_MENU_ITEM.to) return { ...item, alsoActiveFor: [LLP_INFO_MENU_ITEM.to] };
    return item;
  });
  if (kind?.isCommunityInterest) {
    items.push({ ...CIC34_MENU_ITEM, alsoActiveFor: [CIC34_MENU_ITEM.to.replace('cic-34-report', 'cic34-report')] });
  }

  return <SectionNav label={REPORT_SETTINGS_LABEL} items={items} testId="report-settings-nav" wrap />;
}
